import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useAuth } from '../../auth/useAuth';
import {
  loadPublishedProtocols,
  type PublishedProtocol,
} from '../../utils/publishedProtocolsStore';
import { saveProtocolQuestion } from '../../utils/protocolQuestionsStore';

const FOOD_META = {
  GREEN: { label: 'Recommended', bg: '#e8f5e9', borderColor: '#4caf50', chipColor: 'success' as const },
  YELLOW: { label: 'In Moderation', bg: '#fff8e1', borderColor: '#ff9800', chipColor: 'warning' as const },
  RED: { label: 'Limit / Avoid', bg: '#ffebee', borderColor: '#f44336', chipColor: 'error' as const },
};

const FREQ_LABELS = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' } as const;

const formatDate = (value?: string): string => {
  if (!value) return 'Not set';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
};

const getStatusColor = (status: PublishedProtocol['status']): 'success' | 'default' =>
  status === 'ACTIVE' ? 'success' : 'default';

interface ExpandedRowProps {
  protocol: PublishedProtocol;
  patientId?: string;
  patientName: string;
}

interface SentQuestion { text: string; sentAt: string; }

const ExpandedProtocolRow = ({ protocol, patientId, patientName }: ExpandedRowProps) => {
  const [draft, setDraft] = useState('');
  const [composing, setComposing] = useState(false);
  const [sent, setSent] = useState<SentQuestion[]>([]);

  const handleAsk = () => {
    if (!draft.trim()) return;
    saveProtocolQuestion({
      protocolId: protocol.id,
      protocolTitle: protocol.title,
      patientId: patientId ?? '',
      patientName,
      question: draft.trim(),
    });
    setSent((prev) => [{ text: draft.trim(), sentAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }, ...prev]);
    setDraft('');
    setComposing(false);
  };

  return (
    <TableRow>
      <TableCell colSpan={7} sx={{ p: 0, borderBottom: 2, borderColor: 'primary.main' }}>
        <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
          <Grid container spacing={3}>
            {/* Protocol notes */}
            {(protocol.templateNotes ?? []).length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Protocol Notes</Typography>
                <Stack spacing={0.5}>
                  {(protocol.templateNotes ?? []).map((note, i) => (
                    <Typography key={i} variant="body2" color="text.secondary">• {note}</Typography>
                  ))}
                </Stack>
              </Grid>
            )}

            {/* Food guide */}
            {protocol.classifications.length > 0 && (
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" gutterBottom>Food Guide</Typography>
                <Stack spacing={1}>
                  {protocol.classifications.map((food) => {
                    const meta = FOOD_META[food.classification];
                    return (
                      <Box
                        key={food.id}
                        sx={{ p: 1.5, bgcolor: meta.bg, borderLeft: `4px solid ${meta.borderColor}`, borderRadius: 1 }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
                          <Typography variant="body2" fontWeight={600}>{food.customFoodName}</Typography>
                          <Chip label={meta.label} size="small" color={meta.chipColor} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          Portion: {food.portion}{food.notes ? ` · ${food.notes}` : ''}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Grid>
            )}

            {/* Rules */}
            {protocol.rules.length > 0 && (
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" gutterBottom>Nutrition Rules</Typography>
                <Stack spacing={1.5}>
                  {protocol.rules.map((rule, i) => (
                    <Box key={rule.id}>
                      <Typography variant="body2">
                        <Box component="span" fontWeight={700} color="primary.main" sx={{ mr: 0.5 }}>{i + 1}.</Box>
                        {rule.description}
                      </Typography>
                      <Chip label={rule.ruleType.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                    </Box>
                  ))}
                </Stack>
              </Grid>
            )}

            {/* Check-in requirements */}
            {(protocol.checkInTasks ?? []).length > 0 && (
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom>Check-in Requirements</Typography>
                {(['daily', 'weekly', 'monthly'] as const).map((freq) => {
                  const tasks = (protocol.checkInTasks ?? []).filter((t) => t.frequency === freq);
                  if (tasks.length === 0) return null;
                  return (
                    <Box key={freq} sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {FREQ_LABELS[freq]}
                      </Typography>
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        {tasks.map((task) => (
                          <Box key={task.id}>
                            <Typography variant="body2" fontWeight={task.required ? 600 : 400}>
                              {task.required ? '● ' : '○ '}{task.label}{task.required ? ' *' : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 1.5 }}>
                              {task.description}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>* required</Typography>
              </Grid>
            )}

            {/* Ask a question */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2">Ask Your Nutritionist About This Protocol</Typography>
                <Button
                  size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => setComposing(true)}
                  disabled={composing}
                >
                  New Question
                </Button>
              </Stack>

              {/* Sent questions list */}
              {sent.length > 0 && (
                <Stack spacing={1} sx={{ mb: composing ? 2 : 0 }}>
                  {sent.map((q, i) => (
                    <Box
                      key={i}
                      sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, borderLeft: '3px solid', borderLeftColor: 'success.main' }}
                    >
                      <Typography variant="body2">{q.text}</Typography>
                      <Typography variant="caption" color="text.secondary">Sent at {q.sentAt} · Awaiting response</Typography>
                    </Box>
                  ))}
                </Stack>
              )}

              {/* Compose new question */}
              {composing && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
                  <TextField
                    autoFocus
                    placeholder="e.g. Why is whole wheat bread in the yellow category?"
                    multiline
                    rows={2}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Stack spacing={1} sx={{ mt: { xs: 0, sm: 0.5 } }}>
                    <Button variant="contained" onClick={handleAsk} disabled={!draft.trim()} sx={{ whiteSpace: 'nowrap' }}>
                      Send
                    </Button>
                    <Button variant="outlined" onClick={() => { setComposing(false); setDraft(''); }} sx={{ whiteSpace: 'nowrap' }}>
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              )}

              {!composing && sent.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Click <strong>+ New Question</strong> to ask your nutritionist about this protocol.
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export const MyProtocolsPage = () => {
  const { user } = useAuth();
  const allProtocols = loadPublishedProtocols();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const protocols = useMemo(() => {
    if (!user || user.role !== 'PATIENT') return allProtocols;
    const normalizedName = user.displayName.trim().toLowerCase();
    return allProtocols.filter((protocol) => {
      const matchesName = protocol.patientName.trim().toLowerCase() === normalizedName;
      const matchesId = protocol.patientId === user.id;
      return (matchesName || matchesId) && Boolean(protocol.sharedWithPatient);
    });
  }, [allProtocols, user]);

  const activeCount = protocols.filter((p) => p.status === 'ACTIVE').length;
  const inactiveCount = protocols.length - activeCount;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>My Protocols</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Click any protocol row to view the full details and ask your nutritionist a question.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card><CardContent>
            <Typography variant="subtitle2" color="text.secondary">Total Protocols</Typography>
            <Typography variant="h5">{protocols.length}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card><CardContent>
            <Typography variant="subtitle2" color="text.secondary">Active</Typography>
            <Typography variant="h5" color="success.main">{activeCount}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card><CardContent>
            <Typography variant="subtitle2" color="text.secondary">Inactive</Typography>
            <Typography variant="h5">{inactiveCount}</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {protocols.length === 0 ? (
        <Alert severity="info">
          No published protocols found yet. Once a nutritionist publishes a protocol for you, it will appear here.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Protocol</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Foods</TableCell>
                <TableCell>Rules</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {protocols.map((protocol) => {
                const isExpanded = expandedId === protocol.id;
                return (
                  <>
                    <TableRow
                      key={protocol.id}
                      hover
                      onClick={() => setExpandedId(isExpanded ? null : protocol.id)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isExpanded ? 'primary.50' : undefined,
                        '& td': { borderBottom: isExpanded ? 0 : undefined },
                      }}
                    >
                      <TableCell sx={{ width: 40, pr: 0 }}>
                        <IconButton size="small" tabIndex={-1}>
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{protocol.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {protocol.description || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={protocol.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                          size="small"
                          color={getStatusColor(protocol.status)}
                        />
                      </TableCell>
                      <TableCell>{formatDate(protocol.createdDate)}</TableCell>
                      <TableCell>{formatDate(protocol.startDate)}</TableCell>
                      <TableCell>{formatDate(protocol.endDate)}</TableCell>
                      <TableCell>{protocol.classifications.length}</TableCell>
                      <TableCell>{protocol.rules.length}</TableCell>
                    </TableRow>
                    <TableRow key={`${protocol.id}-detail`}>
                      <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isExpanded} unmountOnExit>
                          <ExpandedProtocolRow
                            protocol={protocol}
                            patientId={user?.id}
                            patientName={user?.displayName ?? 'Sarah Johnson'}
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
